import React, { useState } from 'react';
import classNames from 'classnames';

import { AuthTags } from '~constants/authTags';

import { ClickableIcon, Modal, TabContainer } from '~components';
import { faCog } from '~components/icon';

import { useGetProdaConnectionQuery } from '~support-at-home/actions/prodaConnection';

import ProdaConnectionManager from './ProdaConnectionManager';
import SahApiDashboard from '~support-at-home/components/proda/SahApiDashboard';
import SahReferenceDataManager from '~support-at-home/components/proda/SahReferenceDataManager';
import SahRegisteredServices from '~support-at-home/components/proda/SahRegisteredServices';

// Support at Home settings dialog — opened by a cog with a PRODA status badge alongside it.
// Tabbed so further connection/status settings (e.g. Statement Settings) can be added later;
// PRODA is the first tab. Mirrors the Supplier Invoice settings modal structure. Server enforces
// management access (supportathome-proda:Manage); the cog's auth tag governs client visibility.
// "Reference Data" (second tab) shows the GLOBAL Services Australia reference lists — NOT the
// per-client "SaH Approved Services" config tab elsewhere in the app, which manages per-client
// service associations. Same shared modal keeps this reference-data status alongside the PRODA
// connection it depends on, without sitting next to the per-client tab it could be confused with.
// "Registered Services" (third tab) shows this company's own PRODA-sourced registration (TAV-2603).
// "API Dashboard" (fourth tab) surfaces connection/budget/claims/reconciliation/amendment health
// (TAV-2603). Neither new tab needs its own authTag: TabContainer does not filter tabs by authTag,
// and the cog above already gates the whole modal on supportathome-proda:Manage, the same tag the
// new API routes check server-side.
const tabs = [
  { id: 'sah-proda', title: 'PRODA', component: ProdaConnectionManager },
  { id: 'sah-api-dashboard', title: 'API Dashboard', component: SahApiDashboard },
  { id: 'sah-registered-services', title: 'Registered Services', component: SahRegisteredServices },
  { id: 'sah-reference-data', title: 'Reference Data', component: SahReferenceDataManager },
];

const ProdaConnectionSettings: React.FC = () => {
  const { data: connection } = useGetProdaConnectionQuery();
  const [isOpen, setIsOpen] = useState(false);

  const isConnected = connection?.status === 'connected';

  return (
    <div className="d-flex align-items-center gap-1">
      <ClickableIcon
        icon={faCog}
        id="sah-settings-btn"
        toolTip="Support at Home Settings"
        ariaLabel="Support at Home Settings"
        onClick={() => setIsOpen(true)}
        authTag={AuthTags.SUPPORTATHOME.PRODA.MANAGE}
        dataCy="sah-proda-settings-cog"
      />
      {connection !== undefined && (
        <span
          className={classNames('badge', isConnected ? 'bg-success' : 'bg-danger')}
          data-cy="sah-proda-connection-status-badge"
        >
          {isConnected ? 'PRODA Connected' : 'PRODA Not Connected'}
        </span>
      )}
      <Modal setIsOpen={setIsOpen} isOpen={isOpen} header="Support at Home Settings" size="lg" data-cy="sah-proda-modal">
        <TabContainer tabs={tabs} setPageTitle={false} />
      </Modal>
    </div>
  );
};

export default ProdaConnectionSettings;
